// aiScan.js
// ─────────────────────────────────────────────────────────────────────────────
// AI document scan → structured data capture.
//
// The reverse of aiTemplate.js. Instead of turning a layout into a template, the
// user uploads a COMPLETED instruction/variation document (PDF or photo/scan) and
// we ask Claude (via the app's secured anthropicProxy) to read the data values
// OUT of it and return them keyed to the app's canonical fields. The caller then
// drops those values straight into the capture form — so nothing is double-typed.
//
// We deliberately extract data only (never auto-save): an SI/VO is a contract
// record, so the user reviews the pre-filled form before committing it.
// ─────────────────────────────────────────────────────────────────────────────

import { appCheckHeader } from "./appCheck";

const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

function stripFences(s) {
  return String(s).replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
}

function buildContentBlock(file, base64) {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
  return isPdf
    ? { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } }
    : { type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: base64 } };
}

// One authenticated scan call → parsed JSON object (raw, before per-doc filtering).
async function runScan(file, { system, user, maxTokens = 2000 }) {
  if (!AI_PROXY_URL) {
    throw new Error("AI is not configured (REACT_APP_AI_PROXY_URL is unset).");
  }
  if (!file) throw new Error("No file selected.");

  const base64 = await fileToBase64(file);
  const contentBlock = buildContentBlock(file, base64);

  let idToken = "";
  try {
    const { getAuth } = await import("firebase/auth");
    const u = getAuth().currentUser;
    if (u) idToken = await u.getIdToken();
  } catch (_) { /* proxy will reject without a token */ }
  if (!idToken) throw new Error("You must be signed in to scan a document.");

  const res = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, ...(await appCheckHeader()) },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: maxTokens,
      system,
      messages: [{ role: "user", content: [contentBlock, { type: "text", text: user }] }],
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error?.message || data?.error || `Scan failed (${res.status}).`);

  const text = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
  let parsed;
  try {
    parsed = JSON.parse(stripFences(text));
  } catch (_) {
    throw new Error("The document was read but the result couldn't be parsed. Try a clearer copy.");
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("No recognisable fields were found in this document.");
  }
  return parsed;
}

function isBlankValue(v) {
  return v == null || /^(n\/a|na|none provided|enter text…?|tbc|tbd)$/i.test(String(v).trim());
}

// Normalise a money/quantity value to a bare numeric string ("R 1 234.50" → "1234.5").
function toNumberString(v) {
  const s = String(v).replace(/[^0-9.-]/g, "");
  if (!s || s === "-" || s === ".") return "";
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : "";
}

// ═══════════════════════════════════════════════════════════════════════════════
// SITE INSTRUCTION SCAN
// ═══════════════════════════════════════════════════════════════════════════════

const SI_FIELDS = `
  siNumber            – the site-instruction number only (e.g. "022", not "SI No. 022")
  projectNumber       – the employer's agent / project number
  contractNumber      – the contract number
  contractDescription – the contract description / works title
  contractor          – the contractor or JV name
  issuedBy            – who issued the instruction
  subject             – the short subject line
  instructionText     – the full instruction body text
  drawingRevisionRef  – any drawing number + revision referenced (e.g. "0000-0000-DRG-CC-0323-rev1")
  applicablePayItems  – referenced pay items (omit if the field reads "Enter text…" or is blank)
  asBuiltImplications – "Yes" or "No" (read the ticked ☒/☑/X box)
  financialImplications – "Yes" or "No". "Yes" if any of Saving / Admeasure / New Rates / Day Works is ticked; "No" if "No" or "None" is the ticked option
  valuationBasis      – the ticked financial-basis option, exactly one of: Saving | None | Admeasure | New Rates | Day Works
  employersAgentRepresentative – the EA representative name
  instructionDate     – the issue date, formatted YYYY-MM-DD
  instructionTime     – the issue time as HH:MM (24h); omit if "n/a" or blank
  contractorRemarks   – the contractor's remarks (omit if blank)
  receivedBy          – the contractor's representative who received it
  receivedDate        – the received date, formatted YYYY-MM-DD
  receivedTime        – the received time as HH:MM (24h); omit if "n/a" or blank
  distribution        – the distribution / circulation note (omit if blank)
`.trim();

const SI_SYSTEM =
  "You are a construction contract-administration assistant. You read a completed " +
  "Site Instruction document — which may be a clean PDF or a scan/photo — and " +
  "extract its data values as JSON. Tick boxes use ☒/☑/X for ticked and ☐ for " +
  "empty; read the TICKED option. Respond with ONLY a JSON object — no markdown " +
  "fences, no commentary.";

export async function scanInstructionDocument(file) {
  const user =
`Read this document and return the captured data as a flat JSON object using ONLY
these keys (omit any key whose value is blank, "n/a", or placeholder text like
"Enter text…" — do not invent values, do not guess):

${SI_FIELDS}

Rules:
- Dates → "YYYY-MM-DD". Times → "HH:MM" (24-hour) or omit if not a real time.
- Use the document's literal values; do not summarise or rephrase instructionText.
- Return ONLY the JSON object.`;

  const parsed = await runScan(file, { system: SI_SYSTEM, user });

  const allowed = SI_FIELDS.split("\n").map((l) => l.trim().split(/\s/)[0]).filter(Boolean);
  const out = {};
  for (const key of allowed) {
    if (isBlankValue(parsed[key])) continue;
    const v = String(parsed[key]).trim();
    if (v) out[key] = v;
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════════
// VARIATION ORDER SCAN
// ═══════════════════════════════════════════════════════════════════════════════
//
// Each field carries a type so values are normalised the way the VO form expects:
//   num  → bare numeric string (currency symbols / thousands separators stripped)
//   date → YYYY-MM-DD
//   text → trimmed string
// Select-bound fields (status, requestedBy, …) are returned as text and validated
// against the form's option lists by the caller, since the enums live there.

const VO_FIELDS = [
  ["variationNo",          "text", "the variation-order number / reference only"],
  ["requestDate",          "date", "the date the variation was requested or instructed"],
  ["voDescription",        "text", "the short descriptive title of the variation"],
  ["motivation",           "text", "the full motivation / justification text"],
  ["reason",               "text", "a short reason category for the variation"],
  ["requestedBy",          "text", "who initiated it — one of: Employer, Engineer, Contractor"],
  ["fundingSource",        "text", "funding source — one of: Savings, Contingency, Section 116"],
  ["status",              "text", "approval status if stated, e.g. Draft, Instructed, Approved, Implemented"],
  ["employer",             "text", "the employer / client name"],
  ["contractor",           "text", "the contractor or JV name"],
  ["estimatedAdditionalDays", "num", "working days of additional time claimed"],
  ["additionalWorkAmount", "num",  "value of additional work (plain number)"],
  ["existingRatesAmount",  "num",  "value adjusted at existing tender rates (plain number)"],
  ["engineerRef",          "text", "internal engineer reference"],
  ["contractorRef",        "text", "contractor reference number"],
  ["instructionRef",       "text", "the originating site-instruction reference (e.g. SI 022)"],
  ["drawingRef",           "text", "drawing reference(s)"],
  ["specificationRef",     "text", "specification reference"],
  ["clauseRef",            "text", "contract clause reference"],
  ["initiatedBy",          "text", "internal initiator"],
  ["programmeRef",         "text", "programme reference"],
  ["paymentCertificateRef","text", "linked payment-certificate reference"],
  ["dateInstructed",       "date", "date the instruction was issued"],
  ["dateSubmitted",        "date", "date the contractor submitted the quote"],
  ["dateAssessed",         "date", "date the engineer assessed the quote"],
  ["dateApproved",         "date", "date the variation was approved"],
  ["costCategory",        "text", "one of: addition, omission, substitution, remeasurement, provisional adjustment"],
  ["estimatedValue",       "num",  "engineer's initial estimate (plain number)"],
  ["quotedValue",          "num",  "value quoted by the contractor (plain number)"],
  ["assessedValue",        "num",  "value assessed by the engineer (plain number)"],
  ["approvedValue",        "num",  "approved value (plain number)"],
  ["amountPreviouslyCertified", "num", "amount certified in earlier periods (plain number)"],
  ["amountCertifiedThisPeriod", "num", "amount certified this period (plain number)"],
  ["eotClaimedDays",       "num",  "EOT working days claimed"],
  ["eotAssessedDays",      "num",  "EOT working days assessed"],
  ["eotApprovedDays",      "num",  "EOT working days granted"],
  ["criticalPathImpact",   "text", "\"Yes\" or \"No\" — does it affect the critical path"],
  ["notes",                "text", "any internal notes / remarks"],
];

const VO_SYSTEM =
  "You are a construction contract-administration assistant. You read a completed " +
  "Variation Order (VO) / variation document — which may be a clean PDF or a " +
  "scan/photo — and extract its data values as JSON. Amounts must be returned as " +
  "plain numbers (no currency symbols, no thousands separators). Respond with ONLY " +
  "a JSON object — no markdown fences, no commentary.";

export async function scanVariationDocument(file) {
  const fieldList = VO_FIELDS.map(([key, , hint]) => `  ${key} – ${hint}`).join("\n");
  const user =
`Read this Variation Order document and return the captured data as a flat JSON
object using ONLY these keys (omit any key whose value is blank, "n/a", "TBC", or
placeholder text — do not invent values, do not guess):

${fieldList}

Rules:
- Amounts → plain numbers only (e.g. 1234567.89), never "R 1 234 567.89".
- Dates → "YYYY-MM-DD".
- Use the document's literal values; do not summarise voDescription or motivation.
- Return ONLY the JSON object.`;

  const parsed = await runScan(file, { system: VO_SYSTEM, user });

  const out = {};
  for (const [key, type] of VO_FIELDS) {
    if (isBlankValue(parsed[key])) continue;
    if (type === "num") {
      const n = toNumberString(parsed[key]);
      if (n !== "") out[key] = n;
    } else {
      const v = String(parsed[key]).trim();
      if (v) out[key] = v;
    }
  }
  return out;
}
